/**
 * On-device capture quality gate (S7-6; UX_SPEC §3.4 safety condition 4).
 *
 * Assesses the ACTUAL JPEG blob that would be uploaded (post-encode, so the
 * gate sees exactly the bytes the evidence store would receive) on a grayscale
 * downscale (~256px wide — the averaging also suppresses sensor noise that
 * would otherwise inflate the sharpness score in low light). A failed gate
 * BLOCKS the capture with a named reason in calm customer copy — it never
 * advances, never uploads, and never silently passes.
 *
 * ┌─ INTERIM THRESHOLDS — UX GATE ONLY, NOT EVIDENCE ────────────────────────┐
 * │ These floors exist to stop obviously unusable frames (defocus, bad       │
 * │ light, low resolution) BEFORE upload. They are NOT a liveness/PAD or     │
 * │ verification signal, carry no certification claim, and must never be     │
 * │ cited as evidence of anything — verification is the server-side pre-join │
 * │ checks plus the live officer. Calibration is provisional (single-device  │
 * │ reasoning, pending a device-lab sweep across the real handset mix):      │
 * │                                                                          │
 * │ • Resolution floor (shorter side): DOC 600px / SELFIE 480px — per the    │
 * │   S7-6 spec; below this, document text / facial detail does not survive  │
 * │   review at evidence size.                                               │
 * │ • Exposure (mean luma, 0–255): [45, 210] — per the S7-6 spec; below 45   │
 * │   is under-exposed murk, above 210 is blown highlights/glare wash. Runs  │
 * │   BEFORE the sharpness check because bad exposure depresses the          │
 * │   Laplacian score — blaming "blurry" for a dark frame gives the customer │
 * │   the wrong fix.                                                         │
 * │ • Sharpness (variance of 4-neighbour Laplacian on the 256px grayscale):  │
 * │   DOC ≥ 60, SELFIE ≥ 25. Calibration reasoning: deliberate webcam        │
 * │   defocus stays mushy through the downscale (variance well under ~30);   │
 * │   any legible document (text = dense strong edges) scores in the         │
 * │   hundreds-to-thousands, so 60 fails defocus with margin while staying   │
 * │   lenient to genuine captures — a false BLOCK on a genuine customer is   │
 * │   the costlier error at this interim stage. Faces are inherently         │
 * │   low-texture (smooth skin), so in-focus selfies can score as low as     │
 * │   ~40; 25 still fails heavy defocus (<15 typical) without blocking       │
 * │   normal selfies. Document stricter than selfie, per spec.               │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

import type { ArtifactKind } from '@/api/artifacts';

export type QualityReason =
  | 'TOO_SMALL' // capture resolution below the evidence floor
  | 'TOO_DARK' // mean luma under the exposure band
  | 'TOO_BRIGHT' // mean luma over the exposure band (glare/washout)
  | 'TOO_BLURRY' // Laplacian variance under the sharpness floor
  | 'UNREADABLE'; // the blob could not be decoded/analysed — fail CLOSED

export interface QualityMetrics {
  width: number;
  height: number;
  shortSide: number;
  meanLuma: number;
  laplacianVar: number;
}

export interface QualityVerdict {
  ok: boolean;
  reason: QualityReason | null;
  /** Calm customer copy naming the problem and the fix. Null when ok. */
  /** Deck key for the blocking reason — the UI translates it (Tier 2.5). */
  messageKey: string | null;
  metrics: QualityMetrics;
}

/** INTERIM per-kind floors — see the header block before changing anything.
 *  SELFIE_WITH_DOC_FRAME (INTAKE-09 §3): face + document legible in ONE frame —
 *  the document leg needs the DOC resolution floor (text must survive review),
 *  but the co-framed composition mixes low-texture face with high-texture card,
 *  so the sharpness floor sits between the two. INTERIM, UX gate only — the
 *  REAL composite gate (YuNet face leg + OCR doc-legibility leg) is server-side. */
const THRESHOLDS: Record<ArtifactKind, { minShortSide: number; minLaplacianVar: number }> = {
  DOC_PHOTO: { minShortSide: 600, minLaplacianVar: 60 },
  SELFIE_FRAME: { minShortSide: 480, minLaplacianVar: 25 },
  SELFIE_WITH_DOC_FRAME: { minShortSide: 600, minLaplacianVar: 35 },
};
/** INTERIM exposure band (spec-given). Exported for the PRE-shot viewfinder hint
 *  (useViewfinderExposure) so the advisory cue and the post-shot gate can never
 *  disagree about what "too dark" means. */
export const LUMA_MIN = 45; // INTERIM (spec-given band)
export const LUMA_MAX = 210; // INTERIM (spec-given band)
const ANALYSIS_WIDTH = 256;

function messageKeyFor(reason: QualityReason, kind: ArtifactKind): string {
  switch (reason) {
    case 'TOO_SMALL':
      return kind === 'DOC_PHOTO' ? 'droom.quality.small.doc' : 'droom.quality.small.selfie';
    case 'TOO_DARK':
      return 'droom.quality.dark';
    case 'TOO_BRIGHT':
      return 'droom.quality.bright';
    case 'TOO_BLURRY':
      return 'droom.quality.blurry';
    case 'UNREADABLE':
      return 'droom.quality.unreadable';
  }
}

function blocked(reason: QualityReason, kind: ArtifactKind, metrics: QualityMetrics): QualityVerdict {
  return { ok: false, reason, messageKey: messageKeyFor(reason, kind), metrics };
}

interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}

/** Decode the JPEG blob (createImageBitmap, with an <img> fallback). */
async function decode(blob: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    const bmp = await createImageBitmap(blob);
    return {
      source: bmp,
      width: bmp.width,
      height: bmp.height,
      close: () => bmp.close(),
    };
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image decode failed'));
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/**
 * Run the gate on a captured frame. Never throws — an unanalysable frame is a
 * BLOCKED verdict (fail closed: a gate that cannot run must not silently pass).
 */
export async function assessCaptureQuality(
  blob: Blob,
  kind: ArtifactKind,
): Promise<QualityVerdict> {
  const zero: QualityMetrics = { width: 0, height: 0, shortSide: 0, meanLuma: 0, laplacianVar: 0 };
  let decoded: DecodedImage;
  try {
    decoded = await decode(blob);
  } catch {
    return blocked('UNREADABLE', kind, zero);
  }

  try {
    const { width, height } = decoded;
    if (width < 2 || height < 2) return blocked('UNREADABLE', kind, zero);
    const shortSide = Math.min(width, height);

    // Grayscale analysis raster, ~256px wide.
    const scale = Math.min(1, ANALYSIS_WIDTH / width);
    const aw = Math.max(2, Math.round(width * scale));
    const ah = Math.max(2, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = aw;
    canvas.height = ah;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return blocked('UNREADABLE', kind, zero);
    ctx.drawImage(decoded.source, 0, 0, aw, ah);
    const data = ctx.getImageData(0, 0, aw, ah).data;

    // Luma plane (Rec. 601 weights) + mean.
    const n = aw * ah;
    const luma = new Float32Array(n);
    let sum = 0;
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const y =
        0.299 * (data[o] ?? 0) + 0.587 * (data[o + 1] ?? 0) + 0.114 * (data[o + 2] ?? 0);
      luma[i] = y;
      sum += y;
    }
    const meanLuma = sum / n;

    // Variance of the 4-neighbour Laplacian over interior pixels — the
    // classic focus measure: soft (defocused) edges → low variance.
    let lapSum = 0;
    let lapSqSum = 0;
    let count = 0;
    for (let y = 1; y < ah - 1; y++) {
      for (let x = 1; x < aw - 1; x++) {
        const i = y * aw + x;
        const lap =
          (luma[i - 1] ?? 0) +
          (luma[i + 1] ?? 0) +
          (luma[i - aw] ?? 0) +
          (luma[i + aw] ?? 0) -
          4 * (luma[i] ?? 0);
        lapSum += lap;
        lapSqSum += lap * lap;
        count++;
      }
    }
    const lapMean = count > 0 ? lapSum / count : 0;
    const laplacianVar = count > 0 ? lapSqSum / count - lapMean * lapMean : 0;

    const metrics: QualityMetrics = { width, height, shortSide, meanLuma, laplacianVar };
    const t = THRESHOLDS[kind];

    if (shortSide < t.minShortSide) return blocked('TOO_SMALL', kind, metrics);
    if (meanLuma < LUMA_MIN) return blocked('TOO_DARK', kind, metrics);
    if (meanLuma > LUMA_MAX) return blocked('TOO_BRIGHT', kind, metrics);
    if (laplacianVar < t.minLaplacianVar) return blocked('TOO_BLURRY', kind, metrics);
    return { ok: true, reason: null, messageKey: null, metrics };
  } catch {
    return blocked('UNREADABLE', kind, zero);
  } finally {
    decoded.close();
  }
}
